import { useEffect, useState } from 'react';

import { Select } from '../../../../../components/inputs/Select';
import { TextField } from '../../../../../components/inputs/TextField';
import { personnelApi } from '../../personnelApi';
import type { PersonnelCatalogEntry, PersonnelDetail } from '../../personnelTypes';
import { NO_CATALOGS, samplePersonnel } from '../../utils/samplePersonnel';
import { PersonnelSectionModal } from './PersonnelSectionModal';
import { useSectionForm } from './sectionForm';

/**
 * The HMO card's editor — the provider, and the number on the card.
 *
 * **Its own modal since 15 Sep 2026**, split out of the Employment editor in the same change
 * that gave HMO its own panel. The two rows used to sit on the Employment card while the create
 * wizard collected them under Identification, so a field entered on one panel was corrected on
 * another — the panel-mirror rule broken in the one place nobody checks.
 *
 * One card, one Edit: this modal holds the section and nothing else.
 */
export function EditHmoModal({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  record: PersonnelDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [hmoProviderId, setHmoProviderId] = useState<string | null>(null);
  const [hmoProviderName, setHmoProviderName] = useState('');
  const [hmoCardNumber, setHmoCardNumber] = useState('');
  const [providers, setProviders] = useState<PersonnelCatalogEntry[]>([]);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    personnelApi
      .catalogs(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setProviders(next.hmoProviders);
      })
      // A catalog that fails to load leaves the picker empty rather than the modal broken, and
      // `focusValidationError` must not fire for a fetch that named no field.
      .catch(() => undefined);

    return () => controller.abort();
  }, [open]);

  function seed() {
    setHmoProviderId(record.hmoProviderId);
    setHmoProviderName(record.hmoProviderName ?? '');
    setHmoCardNumber(record.hmoCardNumber ?? '');
  }

  /* Autogenerate on an EDIT form — allowed since 14 Sep 2026. */
  function generate() {
    const sample = samplePersonnel({
      catalogs: { ...NO_CATALOGS, hmoProviders: providers },
      identificationTypes: [],
    });

    setHmoProviderId(sample.hmoProviderId);
    setHmoProviderName(sample.hmoProviderName);
    setHmoCardNumber(sample.hmoCardNumber);
  }

  function build() {
    return {
      hmoProviderId,
      hmoCardNumber: hmoCardNumber.trim() || null,
    };
  }

  return (
    <PersonnelSectionModal
      open={open}
      record={record}
      title="Edit HMO"
      idPrefix="personnel-hmo"
      onClose={onClose}
      onSaved={onSaved}
      onOpen={seed}
      onGenerate={generate}
      build={build}
    >
      <Fields
        hmoProviderId={hmoProviderId}
        setHmoProviderId={setHmoProviderId}
        hmoProviderName={hmoProviderName}
        hmoCardNumber={hmoCardNumber}
        setHmoCardNumber={setHmoCardNumber}
        providers={providers}
      />
    </PersonnelSectionModal>
  );
}

function Fields({
  hmoProviderId,
  setHmoProviderId,
  hmoProviderName,
  hmoCardNumber,
  setHmoCardNumber,
  providers,
}: {
  hmoProviderId: string | null;
  setHmoProviderId: (next: string | null) => void;
  hmoProviderName: string;
  hmoCardNumber: string;
  setHmoCardNumber: (next: string) => void;
  providers: PersonnelCatalogEntry[];
}) {
  const { saving, error } = useSectionForm();

  /*
    The stored provider is injected as an option so the field states its value on the first
    paint. `<Select>` resolves its label by finding the value among its options and the catalog
    arrives a moment later — seeded with an id alone the field shows its placeholder, which
    reads as though no provider were set.
  */
  const providerOptions = providers.map((entry) => ({ value: entry.id, label: entry.name }));

  if (hmoProviderId && !providers.some((entry) => entry.id === hmoProviderId)) {
    providerOptions.unshift({ value: hmoProviderId, label: hmoProviderName || '…' });
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Select
        id="personnel-hmo-hmo-provider"
        label="HMO provider"
        value={hmoProviderId ?? ''}
        onChange={(next) => setHmoProviderId(next || null)}
        options={providerOptions}
        error={error?.fieldError('hmoProviderId')}
        disabled={saving}
      />

      <TextField
        id="personnel-hmo-hmo-card-number"
        label="HMO card number"
        value={hmoCardNumber}
        onChange={(e) => setHmoCardNumber(e.target.value)}
        error={error?.fieldError('hmoCardNumber')}
        disabled={saving}
      />
    </div>
  );
}
